/* eslint-disable @typescript-eslint/no-use-before-define */
import React from 'react';
import {InvariantError} from 'ts-invariant';
import {styled} from '@mui/material/styles';
import {ProgressBar} from './ProgressBar';

export enum STRENGTH_LEVEL {
  BLANK,
  TOO_SHORT,
  WEAK,
  OK,
  GOOD,
  GREAT,
}

type StrengthLevelDetail = {label: string; color: string; progress: number};
type StrengthLevelDetails = Map<STRENGTH_LEVEL, StrengthLevelDetail>;

type Props = {
  level: STRENGTH_LEVEL;
  strengthLevels: StrengthLevelDetails;
};

const Wrapper = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const ProgressWrapper = styled('div')({
  width: '83%',
});

const Label = styled('div')<{color: string}>(({color}) => ({
  color,
  fontSize: '10px',
  width: '17%',
  textAlign: 'center',
}));

const defaultStrengthLevels: StrengthLevelDetails = new Map<
  STRENGTH_LEVEL,
  {label: string; color: string; progress: number}
>([
  [STRENGTH_LEVEL.BLANK, {label: 'Blank', color: '#9B9B9B', progress: 0}],
  [
    STRENGTH_LEVEL.TOO_SHORT,
    {label: 'Too short', color: '#FF3539', progress: 0.2},
  ],
  [STRENGTH_LEVEL.WEAK, {label: 'Too weak', color: '#FF3539', progress: 0.33}],
  [STRENGTH_LEVEL.OK, {label: 'So so', color: '#F5A623', progress: 0.5}],
  [STRENGTH_LEVEL.GOOD, {label: 'Good', color: '#7ED321', progress: 0.75}],
  [STRENGTH_LEVEL.GREAT, {label: 'Great', color: '#59AD00', progress: 1}],
]);

export function PasswordStrengthMeter(props: Props) {
  if (!props.strengthLevels.has(props.level)) {
    throw new InvariantError(
      `Tried to access password strength level details that wasn't provided to PasswordStrengthMeter: Strength Level: ${props.level}.`,
    );
  }

  const details = props.strengthLevels.get(props.level) as StrengthLevelDetail; // The .has() check above means we'll definitely have details here.

  return (
    <Wrapper>
      <ProgressWrapper>
        <ProgressBar
          progress={details.progress}
          barHeight={3}
          color={details.color}
        />
      </ProgressWrapper>
      <Label color={details.color}>{details.label}</Label>
    </Wrapper>
  );
}

PasswordStrengthMeter.defaultProps = {
  strengthLevels: defaultStrengthLevels,
};
